<script setup lang="ts">
/**
 * ConfigureView — `/configure` route (Story 302).
 *
 * Renders a Pajamas-styled form that captures every field of a `ClaudeConfig`
 * and supports three actions: test the connection, save the form to storage,
 * clear the saved form. The form uses `claudeConfigSchema` from the library
 * for validation so the runtime validator and the UI never drift.
 */
import { computed, reactive, ref, watch } from 'vue';
import { ZodError } from 'zod';
import {
  ComboboxRoot,
  ComboboxAnchor,
  ComboboxInput,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxItem,
  ComboboxEmpty,
} from 'reka-ui';
import PageHeader from '../components/PageHeader.vue';
import Card from '../components/Card.vue';
import Banner from '../components/Banner.vue';
import GlIcon from '../components/GlIcon.vue';
import {
  loadPersistedConfig,
  savePersistedConfig,
  clearPersistedConfig,
  type PersistableFormState,
} from '../services/configStore';
import { runHealthCheck, type HealthCheckResult, type ProviderFactory } from '../services/healthChecker';
import { makeBackendChatProvider } from '../services/backendChatProvider';
import { fetchModels, type ModelOption } from '../services/modelsProvider';
import { claudeConfigSchema, DEFAULT_CLAUDE_BASE_URL, DEFAULT_ANTHROPIC_VERSION } from '@lib/claude-config';
import type { ClaudeConfig } from '@lib/claude-config';

interface FormErrors {
  apiKey?: string;
  model?: string;
  maxTokens?: string;
  temperature?: string;
  baseUrl?: string;
  anthropicVersion?: string;
  global?: string;
}

// App-local on purpose: `claude-config.ts` keeps `model` as an open string
// (not an enum) because Anthropic's model catalog changes independently of
// this library's release cadence, so a curated list belongs to the app, not the lib.
const CURATED_MODELS = [
  { id: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
];
const DEFAULT_MODEL_ID = CURATED_MODELS[0]!.id;

const props = defineProps<{
  providerFactory?: ProviderFactory;
  storageOverrides?: { local?: Storage; session?: Storage };
  modelsFetcher?: (apiKey: string) => Promise<ModelOption[]>;
}>();

const initial = loadPersistedConfig(
  props.storageOverrides?.local,
  props.storageOverrides?.session,
);

const form = reactive<PersistableFormState>({
  apiKey: initial.apiKey ?? '',
  model: initial.model ?? DEFAULT_MODEL_ID,
  maxTokens: typeof initial.maxTokens === 'number' ? initial.maxTokens : 1024,
  temperature: typeof initial.temperature === 'number' ? initial.temperature : 0.7,
  baseUrl: initial.baseUrl ?? DEFAULT_CLAUDE_BASE_URL,
  anthropicVersion: initial.anthropicVersion ?? DEFAULT_ANTHROPIC_VERSION,
});

const errors = reactive<FormErrors>({});
const showApiKey = ref(false);
const advancedOpen = ref(false);
const isTesting = ref(false);
const isSaving = ref(false);
const result = ref<HealthCheckResult | null>(null);
const savedAt = ref<string | null>(null);

// Default (uninjected) Test Connection path: relay the entered key to this
// app's own backend over `/api/messages` (Story 015.3b AC1) instead of
// constructing a browser-side Anthropic client. Adapts
// `backendChatProvider.ts`'s `ChatProvider` shape to the connect/healthCheck
// shape `healthChecker.ts` expects, so `runHealthCheck()` needs no changes.
function defaultTestConnectionFactory(): {
  connect(config: ClaudeConfig): Promise<void>;
  disconnect(): Promise<void>;
  healthCheck(): Promise<{ ok: boolean; latencyMs: number; message?: string }>;
} {
  let apiKey = '';
  let model: string | undefined;
  let maxTokens: number | undefined;
  return {
    async connect(config) {
      apiKey = config.auth.key;
      model = config.model;
      maxTokens = config.maxTokens;
    },
    async disconnect() {},
    async healthCheck() {
      const start = performance.now();
      const chat = makeBackendChatProvider({ apiKey, model, maxTokens });
      await chat.complete({ history: [{ role: 'user', content: 'ping' }] });
      return { ok: true, latencyMs: performance.now() - start, message: 'Connection healthy.' };
    },
  };
}

const factory: ProviderFactory = props.providerFactory ?? defaultTestConnectionFactory;

// connect-with-claude-8bn — replace the curated fallback with the real list
// Anthropic reports as available to the caller's key, via the backend proxy
// (never called directly from the browser). Falls back to CURATED_MODELS
// whenever there is no key yet, the fetch is in flight, or it fails.
const modelsFetcherFn = props.modelsFetcher ?? ((apiKey: string) => fetchModels(apiKey));
const modelOptions = ref<ModelOption[]>(CURATED_MODELS);

// Code Review finding on MR !72 (also flagged by GitLab Duo): a slow,
// superseded fetch resolving after a newer one must not clobber the newer
// result. `fetchSequence` is a monotonic request counter — a response is
// only applied when it's still the most-recently-started request ("last
// request started wins", not "last response to arrive wins").
let fetchSequence = 0;

watch(
  () => form.apiKey,
  (value) => {
    const trimmed = (value ?? '').trim();
    const sequence = ++fetchSequence;
    modelOptions.value = CURATED_MODELS;
    if (!trimmed) return;
    modelsFetcherFn(trimmed)
      .then((models) => {
        if (sequence !== fetchSequence) return;
        modelOptions.value = models;
      })
      .catch(() => {
        if (sequence !== fetchSequence) return;
        modelOptions.value = CURATED_MODELS;
      });
  },
  { immediate: true },
);

const submittable = computed(() => {
  return Boolean(form.apiKey && form.model && form.maxTokens && form.maxTokens > 0);
});

function clearErrors(): void {
  errors.apiKey = undefined;
  errors.model = undefined;
  errors.maxTokens = undefined;
  errors.temperature = undefined;
  errors.baseUrl = undefined;
  errors.anthropicVersion = undefined;
  errors.global = undefined;
}

function validate(): ClaudeConfig | null {
  clearErrors();
  const candidate = {
    provider: 'claude' as const,
    // Trim the pasted key: API keys copied from a terminal or password
    // manager frequently carry a trailing newline / surrounding whitespace.
    // Sent verbatim, that produces a genuine 401 ("invalid x-api-key") even
    // though the key works externally — the SDK reports it as "auth failed".
    auth: { type: 'api_key' as const, key: (form.apiKey ?? '').trim() },
    baseUrl: form.baseUrl && form.baseUrl.length > 0 ? form.baseUrl : DEFAULT_CLAUDE_BASE_URL,
    model: form.model ?? '',
    maxTokens: typeof form.maxTokens === 'number' ? form.maxTokens : 0,
    temperature: form.temperature,
    anthropicVersion: form.anthropicVersion && form.anthropicVersion.length > 0
      ? form.anthropicVersion
      : DEFAULT_ANTHROPIC_VERSION,
    timeoutMs: 30000,
  };
  const parsed = claudeConfigSchema.safeParse(candidate);
  if (parsed.success) {
    return parsed.data;
  }
  applyZodErrors(parsed.error);
  return null;
}

function applyZodErrors(error: ZodError): void {
  for (const issue of error.issues) {
    const head = issue.path[0];
    if (head === 'auth') errors.apiKey = issue.message;
    else if (head === 'model') errors.model = issue.message;
    else if (head === 'maxTokens') errors.maxTokens = issue.message;
    else if (head === 'temperature') errors.temperature = issue.message;
    else if (head === 'baseUrl') errors.baseUrl = issue.message;
    else if (head === 'anthropicVersion') errors.anthropicVersion = issue.message;
    else errors.global = issue.message;
  }
}

async function onTest(): Promise<void> {
  result.value = null;
  const config = validate();
  if (!config) return;
  isTesting.value = true;
  try {
    const outcome = await runHealthCheck(config, factory);
    result.value = outcome.ok
      ? outcome
      : { ...outcome, message: withAttemptedHost(outcome.message, config.baseUrl) };
  } finally {
    isTesting.value = false;
  }
}

// Surfaces which host was actually attempted, since the SDK's error
// messages (e.g. "Connection error.") give no hint that `baseUrl` is
// the suspect field. `baseUrl` may be momentarily invalid mid-edit, so
// fall back to the unmodified message if it can't be parsed.
function withAttemptedHost(message: string, baseUrl: string): string {
  try {
    return `${message} (${new URL(baseUrl).host})`;
  } catch {
    return message;
  }
}

function onSave(): void {
  const config = validate();
  if (!config) return;
  isSaving.value = true;
  try {
    savePersistedConfig(form, props.storageOverrides?.local, props.storageOverrides?.session);
    savedAt.value = new Date().toISOString();
  } finally {
    isSaving.value = false;
  }
}

function onResetBaseUrl(): void {
  form.baseUrl = DEFAULT_CLAUDE_BASE_URL;
  savePersistedConfig(form, props.storageOverrides?.local, props.storageOverrides?.session);
  result.value = null;
}

function onClear(): void {
  clearPersistedConfig(props.storageOverrides?.local, props.storageOverrides?.session);
  form.apiKey = '';
  form.model = DEFAULT_MODEL_ID;
  form.maxTokens = 1024;
  form.temperature = 0.7;
  form.baseUrl = DEFAULT_CLAUDE_BASE_URL;
  form.anthropicVersion = DEFAULT_ANTHROPIC_VERSION;
  advancedOpen.value = false;
  result.value = null;
  savedAt.value = null;
  clearErrors();
}
</script>

<template>
  <div>
    <PageHeader
      title="Connection configuration"
      subtitle="Build a ClaudeConfig and test the connection. Your key is sent to this app's own backend per-request over TLS to call Anthropic server-side; it is not persisted server-side."
    />

    <Banner variant="warning" title="Demo storage">
      The API key is kept in <code>sessionStorage</code> for the lifetime of
      this tab, and is also sent to this app's own backend per-request over
      TLS to call Anthropic server-side; it is not persisted server-side. Do
      not paste production keys.
    </Banner>

    <Card title="Form">
      <form class="cwc-config-form" @submit.prevent="onTest">
        <div class="cwc-config-form__row">
          <label for="apiKey">API key</label>
          <div class="cwc-config-form__input-group" data-testid="api-key-input-group">
            <input
              id="apiKey"
              v-model="form.apiKey"
              :type="showApiKey ? 'text' : 'password'"
              autocomplete="off"
              spellcheck="false"
              data-testid="api-key-input"
            />
            <button
              type="button"
              class="cwc-config-form__toggle"
              :aria-pressed="showApiKey"
              :aria-label="showApiKey ? 'Hide API key' : 'Show API key'"
              data-testid="api-key-reveal-toggle"
              @click="showApiKey = !showApiKey"
            >
              <GlIcon :name="showApiKey ? 'eye-slash' : 'eye'" />
            </button>
          </div>
          <p v-if="errors.apiKey" class="cwc-config-form__error" role="alert">{{ errors.apiKey }}</p>
        </div>

        <div class="cwc-config-form__row">
          <label id="model-label" for="model">Model</label>
          <ComboboxRoot v-model="form.model" class="cwc-combobox">
            <ComboboxAnchor class="cwc-combobox__anchor">
              <ComboboxInput
                id="model"
                v-model="form.model"
                aria-labelledby="model-label"
                class="cwc-combobox__input"
                data-testid="model-input"
              />
              <ComboboxTrigger
                class="cwc-combobox__trigger"
                aria-label="Show model options"
                data-testid="model-combobox-trigger"
              >
                <GlIcon name="chevron-down" />
              </ComboboxTrigger>
            </ComboboxAnchor>
            <ComboboxContent class="cwc-combobox__content">
              <ComboboxEmpty class="cwc-combobox__empty" data-testid="model-empty-state">
                No matching model — press Enter to use this value as a custom model ID.
              </ComboboxEmpty>
              <ComboboxItem
                v-for="curated in modelOptions"
                :key="curated.id"
                :value="curated.id"
                :text-value="`${curated.label} ${curated.id}`"
                class="cwc-combobox__item"
                :data-testid="`model-option-${curated.id}`"
              >
                <span class="cwc-combobox__item-label">{{ curated.label }}</span>
                <GlIcon
                  v-if="form.model === curated.id"
                  name="check"
                  class="cwc-combobox__item-check"
                />
              </ComboboxItem>
            </ComboboxContent>
          </ComboboxRoot>
          <p v-if="errors.model" class="cwc-config-form__error" role="alert">{{ errors.model }}</p>
        </div>

        <div class="cwc-config-form__row">
          <label for="maxTokens">Max tokens</label>
          <input
            id="maxTokens"
            v-model.number="form.maxTokens"
            type="number"
            min="1"
            max="4096"
            step="1"
            data-testid="max-tokens-input"
          />
          <p v-if="errors.maxTokens" class="cwc-config-form__error" role="alert">{{ errors.maxTokens }}</p>
        </div>

        <div class="cwc-config-form__row">
          <label for="temperature">Temperature ({{ form.temperature }})</label>
          <input
            id="temperature"
            v-model.number="form.temperature"
            type="range"
            min="0"
            max="1"
            step="0.05"
            data-testid="temperature-input"
          />
          <p v-if="errors.temperature" class="cwc-config-form__error" role="alert">{{ errors.temperature }}</p>
        </div>

        <div class="cwc-config-form__row">
          <label for="baseUrl">Base URL</label>
          <input
            id="baseUrl"
            v-model="form.baseUrl"
            type="url"
            data-testid="base-url-input"
          />
          <button
            v-if="form.baseUrl !== DEFAULT_CLAUDE_BASE_URL"
            type="button"
            class="cwc-config-form__reset-link"
            data-testid="reset-base-url"
            @click="onResetBaseUrl"
          >
            Reset to default ({{ DEFAULT_CLAUDE_BASE_URL }})
          </button>
          <p class="cwc-config-form__hint" data-testid="base-url-hint">
            Default: {{ DEFAULT_CLAUDE_BASE_URL }}
          </p>
          <p v-if="errors.baseUrl" class="cwc-config-form__error" role="alert">{{ errors.baseUrl }}</p>
        </div>

        <div class="cwc-config-form__row">
          <button
            type="button"
            class="cwc-config-form__advanced-toggle"
            data-testid="advanced-section-toggle"
            :aria-expanded="advancedOpen"
            @click="advancedOpen = !advancedOpen"
          >
            {{ advancedOpen ? 'Hide advanced options' : 'Show advanced options' }}
          </button>
          <div v-if="advancedOpen" class="cwc-config-form__row">
            <label for="anthropicVersion">Anthropic version</label>
            <input
              id="anthropicVersion"
              v-model="form.anthropicVersion"
              type="text"
              data-testid="anthropic-version-input"
            />
            <p v-if="errors.anthropicVersion" class="cwc-config-form__error" role="alert">{{ errors.anthropicVersion }}</p>
          </div>
        </div>

        <p v-if="errors.global" class="cwc-config-form__error" role="alert">{{ errors.global }}</p>

        <div class="cwc-config-form__actions">
          <button
            type="submit"
            class="cwc-config-form__primary"
            :disabled="!submittable || isTesting"
            data-testid="test-connection-button"
          >
            <span v-if="isTesting" class="cwc-config-form__spinner" aria-hidden="true" />
            {{ isTesting ? 'Testing...' : 'Test connection' }}
          </button>
          <button
            type="button"
            class="cwc-config-form__secondary"
            :disabled="!submittable || isSaving"
            data-testid="save-button"
            @click="onSave"
          >
            Save
          </button>
          <button
            type="button"
            class="cwc-config-form__tertiary"
            data-testid="clear-button"
            @click="onClear"
          >
            Clear
          </button>
        </div>
      </form>
    </Card>

    <div v-if="result" class="cwc-config-result">
      <Banner
        :variant="result.ok ? 'success' : 'danger'"
        :title="result.ok ? 'Connection healthy' : 'Connection failed'"
      >
        {{ result.message }}
        <span class="cwc-config-result__latency" data-testid="latency-display">
          ({{ result.latencyMs }} ms)
        </span>
      </Banner>
    </div>

    <div v-if="savedAt" class="cwc-config-saved">
      <Banner variant="info" title="Saved">
        Saved at {{ savedAt }}.
      </Banner>
    </div>
  </div>
</template>

<style scoped>
.cwc-config-form {
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-4);
}

.cwc-config-form__row {
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-1);
}

.cwc-config-form__row label {
  color: var(--gl-fg-default);
  font-weight: var(--gl-font-weight-bold);
  font-size: var(--gl-font-size-sm);
}

.cwc-config-form__row input[type='text'],
.cwc-config-form__row input[type='url'],
.cwc-config-form__row input[type='password'],
.cwc-config-form__row input[type='number'] {
  appearance: none;
  background: var(--gl-bg-surface);
  color: var(--gl-fg-default);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  font: inherit;
  width: 100%;
}

.cwc-config-form__advanced-toggle {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  color: var(--gl-accent-primary);
  font: inherit;
  font-size: var(--gl-font-size-sm);
  text-decoration: underline;
  cursor: pointer;
  align-self: flex-start;
}

.cwc-config-form__reset-link {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  color: var(--gl-accent-primary);
  font: inherit;
  font-size: var(--gl-font-size-sm);
  text-decoration: underline;
  cursor: pointer;
  align-self: flex-start;
}

.cwc-config-form__input-group {
  display: flex;
  align-items: stretch;
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  background: var(--gl-bg-surface);
  overflow: hidden;
}

.cwc-config-form__input-group input[type='password'],
.cwc-config-form__input-group input[type='text'] {
  flex: 1;
  border: none;
  border-radius: 0;
}

.cwc-config-form__toggle {
  appearance: none;
  background: none;
  border: none;
  border-left: var(--gl-border-width) solid var(--gl-border-default);
  color: var(--gl-fg-subtle);
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cwc-config-form__toggle:hover {
  color: var(--gl-fg-default);
  background: var(--gl-bg-surface-subtle);
}

.cwc-config-form__toggle:focus-visible {
  outline: none;
  box-shadow: var(--gl-focus-ring);
}

.cwc-config-form__secondary,
.cwc-config-form__tertiary {
  appearance: none;
  background: var(--gl-bg-surface);
  color: var(--gl-fg-default);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-4);
  font: inherit;
  cursor: pointer;
}

.cwc-config-form__primary {
  appearance: none;
  background: var(--gl-accent-primary);
  color: var(--gl-accent-on-primary);
  border: var(--gl-border-width) solid var(--gl-accent-primary);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-5);
  font: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--gl-spacing-2);
}

.cwc-config-form__primary:disabled,
.cwc-config-form__secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cwc-config-form__actions {
  display: flex;
  gap: var(--gl-spacing-2);
  flex-wrap: wrap;
}

.cwc-config-form__error {
  margin: 0;
  color: var(--gl-status-danger-fg);
  font-size: var(--gl-font-size-sm);
}

.cwc-config-form__hint {
  margin: 0;
  color: var(--gl-fg-subtle);
  font-size: var(--gl-font-size-sm);
}

/* :deep() is required here (connect-with-claude-6k7): reka-ui's
 * ComboboxRoot renders `.cwc-combobox` internally, several component
 * layers below where the class is applied in this template, so Vue's
 * scoped-style compiler never stamps this element with the SFC's
 * data-v-* attribute. A plain `.cwc-combobox { position: relative }`
 * rule compiles to `.cwc-combobox[data-v-xxx]`, which never matches the
 * real DOM node — position: relative silently never applied, so the
 * `.cwc-combobox__content` dropdown (which *does* get the data-v-*
 * attribute, since it's slot content authored directly in this
 * template) fell back to the page's initial containing block instead
 * of anchoring under the input. :deep() compiles to a descendant
 * selector instead, which only requires the attribute on an ancestor. */
:deep(.cwc-combobox) {
  position: relative;
}

.cwc-combobox__anchor {
  display: flex;
  align-items: stretch;
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  background: var(--gl-bg-surface);
  overflow: hidden;
}

.cwc-combobox__anchor:has(.cwc-combobox__input:focus) {
  box-shadow: var(--gl-focus-ring);
}

.cwc-combobox__input {
  flex: 1;
  appearance: none;
  background: none;
  color: var(--gl-fg-default);
  border: none;
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  font: inherit;
  width: 100%;
}

.cwc-combobox__input:focus {
  outline: none;
}

.cwc-combobox__trigger {
  appearance: none;
  background: none;
  border: none;
  border-left: var(--gl-border-width) solid var(--gl-border-default);
  color: var(--gl-fg-subtle);
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cwc-combobox__trigger:hover {
  color: var(--gl-fg-default);
  background: var(--gl-bg-surface-subtle);
}

.cwc-combobox__trigger:focus-visible {
  outline: none;
  box-shadow: var(--gl-focus-ring);
}

.cwc-combobox__content {
  position: absolute;
  top: calc(100% + var(--gl-spacing-1));
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 16rem;
  overflow-y: auto;
  background: var(--gl-bg-surface);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  box-shadow: var(--gl-shadow-md);
  padding: var(--gl-spacing-1);
}

.cwc-combobox__empty {
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  color: var(--gl-fg-subtle);
  font-size: var(--gl-font-size-sm);
}

.cwc-combobox__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gl-spacing-2);
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  border-radius: var(--gl-border-radius-base);
  cursor: pointer;
  color: var(--gl-fg-default);
}

.cwc-combobox__item[data-highlighted] {
  background: var(--gl-bg-surface-subtle);
}

.cwc-combobox__item[aria-selected='true'] {
  font-weight: var(--gl-font-weight-bold);
}

.cwc-combobox__item-check {
  color: var(--gl-accent-primary);
}

.cwc-config-form__spinner {
  width: var(--gl-spacing-3);
  height: var(--gl-spacing-3);
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: cwc-spin var(--gl-duration-medium) linear infinite;
}

@keyframes cwc-spin {
  to { transform: rotate(360deg); }
}

.cwc-config-result,
.cwc-config-saved {
  margin-top: var(--gl-spacing-4);
}

.cwc-config-result__latency {
  margin-left: var(--gl-spacing-2);
  font-family: var(--gl-font-family-mono);
}
</style>
